import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { VideoWithTags } from '../types/video';
import { ArrowLeft, Star, Clock, Eye } from 'lucide-react';

export default function VideoPlayer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [video, setVideo] = useState<VideoWithTags | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<VideoWithTags[]>([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  useEffect(() => {
    const category = searchParams.get('category');
    setCategoryId(category);

    if (category && !id) {
      loadFirstVideoFromCategory(category);
    } else if (id) {
      loadVideo();
      incrementViewCount();
    }
  }, [id, searchParams]);

  async function loadFirstVideoFromCategory(categoryTagId: string) {
    try {
      const { data: videoTagData } = await supabase
        .from('video_tags')
        .select('video_id')
        .eq('tag_id', categoryTagId);

      if (!videoTagData || videoTagData.length === 0) {
        setLoading(false);
        return;
      }

      const videoIds = videoTagData.map((vt) => vt.video_id);

      const { data: videosData } = await supabase
        .from('videos')
        .select('*')
        .in('id', videoIds)
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(1);

      if (!videosData || videosData.length === 0) {
        setLoading(false);
        return;
      }

      const firstVideo = videosData[0];
      navigate(`/dashboard/videos/${firstVideo.id}?category=${categoryTagId}`, { replace: true });
    } catch (error) {
      console.error('Error loading first video from category:', error);
      setLoading(false);
    }
  }

  async function loadVideo() {
    try {
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('*')
        .eq('id', id)
        .single();

      if (videoError) throw videoError;

      const { data: tagData } = await supabase
        .from('video_tags')
        .select('tag_id, tags(*)')
        .eq('video_id', id);

      const videoWithTags = {
        ...videoData,
        tags: tagData?.map((vt: any) => vt.tags) || [],
      };

      setVideo(videoWithTags);

      const tagIds = categoryId
        ? [categoryId]
        : videoWithTags.tags?.map((t) => t.id) || [];

      await Promise.all([
        loadFavoriteStatus(),
        loadProgress(),
        loadRelatedVideos(tagIds),
      ]);
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadFavoriteStatus() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('user_favorites')
      .select('id')
      .eq('user_id', user.id)
      .eq('video_id', id)
      .maybeSingle();

    if (!error && data) {
      setIsFavorite(true);
    }
  }

  async function loadProgress() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('user_video_progress')
      .select('progress_seconds')
      .eq('user_id', user.id)
      .eq('video_id', id)
      .maybeSingle();

    if (!error && data) {
      setProgress(data.progress_seconds);
    }
  }

  async function loadRelatedVideos(tagIds: string[]) {
    if (tagIds.length === 0) return;

    const { data: relatedTagData } = await supabase
      .from('video_tags')
      .select('video_id')
      .in('tag_id', tagIds)
      .neq('video_id', id);

    const relatedVideoIds = [...new Set(relatedTagData?.map((vt) => vt.video_id) || [])];

    if (relatedVideoIds.length === 0) return;

    const { data: videosData, error } = await supabase
      .from('videos')
      .select('*')
      .in('id', relatedVideoIds)
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) throw error;

    const videosWithTags = await Promise.all(
      (videosData || []).map(async (video) => {
        const { data: tagData } = await supabase
          .from('video_tags')
          .select('tag_id, tags(*)')
          .eq('video_id', video.id);

        return {
          ...video,
          tags: tagData?.map((vt: any) => vt.tags) || [],
        };
      })
    );

    setRelatedVideos(videosWithTags);
  }

  async function incrementViewCount() {
    try {
      const { data: videoData } = await supabase
        .from('videos')
        .select('view_count')
        .eq('id', id)
        .single();

      if (videoData) {
        await supabase
          .from('videos')
          .update({ view_count: videoData.view_count + 1 })
          .eq('id', id);
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  }

  async function toggleFavorite() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      if (isFavorite) {
        await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('video_id', id);
        setIsFavorite(false);
      } else {
        await supabase
          .from('user_favorites')
          .insert({ user_id: user.id, video_id: id });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }

  async function updateProgress(seconds: number) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !video) return;

    try {
      const completed = seconds >= video.duration * 0.9;

      const { data: existing } = await supabase
        .from('user_video_progress')
        .select('id')
        .eq('user_id', user.id)
        .eq('video_id', id)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('user_video_progress')
          .update({
            progress_seconds: seconds,
            completed,
            last_watched_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
      } else {
        await supabase.from('user_video_progress').insert({
          user_id: user.id,
          video_id: id,
          progress_seconds: seconds,
          completed,
        });
      }

      setProgress(seconds);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (loading || !video) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading video...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard/videos')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Library
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="aspect-video bg-black">
                <iframe
                  src={video.adilo_url}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onLoad={() => {
                    const interval = setInterval(() => {
                      updateProgress(progress + 5);
                    }, 5000);
                    return () => clearInterval(interval);
                  }}
                />
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {video.title}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Eye size={16} />
                        {video.view_count} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        {formatDuration(video.duration)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={toggleFavorite}
                    className={`p-3 rounded-full transition-all ${
                      isFavorite
                        ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Star size={24} className={isFavorite ? 'fill-current' : ''} />
                  </button>
                </div>

                {video.tags && video.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {video.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          color: tag.color,
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {video.description && (
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">{video.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            {relatedVideos.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {categoryId ? 'Videos in this Category' : 'Related Videos'}
                </h2>
                <div className="space-y-4">
                  {relatedVideos.map((relatedVideo) => (
                    <div
                      key={relatedVideo.id}
                      onClick={() => {
                        const url = categoryId
                          ? `/dashboard/videos/${relatedVideo.id}?category=${categoryId}`
                          : `/dashboard/videos/${relatedVideo.id}`;
                        navigate(url);
                      }}
                      className="group cursor-pointer"
                    >
                      <div className="flex gap-3">
                        <div className="relative w-32 h-20 bg-gray-100 rounded flex-shrink-0 overflow-hidden">
                          {relatedVideo.thumbnail_url ? (
                            <img
                              src={relatedVideo.thumbnail_url}
                              alt={relatedVideo.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Eye size={24} className="text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors mb-1">
                            {relatedVideo.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {formatDuration(relatedVideo.duration)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
